import React from 'react';

const VIZ_MODES = [
  { key: 'standard', label: 'Standard', icon: 'fas fa-braille', accent: '#047857' },
  { key: 'individual', label: 'Individual', icon: 'fas fa-circle', accent: '#f59e0b' },
  { key: 'heatmap', label: 'Heatmap', icon: 'fas fa-fire', accent: '#ef4444' },
  { key: 'fractal', label: 'Fractal', icon: 'fas fa-sitemap', accent: '#FDE68A' },
  { key: 'voronoi', label: 'Voronoi', icon: 'fas fa-draw-polygon', accent: '#10b981' },
];

const SearchVizControls = ({
  searchVizMode,
  setSearchVizMode,
  comparisonMode,
  setComparisonMode,
  comparisonVizLeft,
  setComparisonVizLeft,
  comparisonVizRight,
  setComparisonVizRight,
  dotColorMode,
  setDotColorMode
}) => {
  const activeMode = comparisonMode ? comparisonVizLeft : searchVizMode;
  const setMode = comparisonMode ? setComparisonVizLeft : setSearchVizMode;
  const showColorToggle = activeMode === 'individual' || (comparisonMode && comparisonVizRight === 'individual');

  return (
    <>
      {/* Advanced Visualization Mode — Radio Buttons */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 12, background: '#334155',
        borderRadius: 12, border: '1px solid rgba(252, 211, 77,0.25)', overflow: 'hidden',
        flexWrap: 'wrap'
      }} data-testid="search-advanced-viz-panel">
        <div style={{
          padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 6,
          borderRight: '1px solid rgba(252, 211, 77,0.15)',
          background: 'rgba(252, 211, 77,0.08)'
        }}>
          <i className="fas fa-layer-group" style={{ color: '#047857', fontSize: '0.72rem' }} />
          <span style={{ color: '#047857', fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
            Visualization
          </span>
        </div>
        {VIZ_MODES.map((mode) => {
          const isActive = activeMode === mode.key;
          return (
            <label
              key={mode.key}
              data-testid={`search-viz-radio-${mode.key}`}
              style={{
                padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
                background: isActive ? `${mode.accent}18` : 'transparent',
                color: isActive ? '#e2e8f0' : '#71717a',
                fontSize: '0.75rem', fontWeight: isActive ? 700 : 500,
                borderRight: '1px solid rgba(252, 211, 77,0.1)',
                transition: 'all 0.25s', userSelect: 'none',
                borderBottom: isActive ? `2px solid ${mode.accent}` : '2px solid transparent',
              }}
            >
              <input
                type="radio"
                name="searchVizMode"
                value={mode.key}
                checked={isActive}
                onChange={() => setMode(mode.key)}
                style={{ accentColor: mode.accent, width: 13, height: 13, cursor: 'pointer' }}
              />
              <i className={mode.icon} style={{ fontSize: '0.68rem', color: isActive ? mode.accent : '#71717a' }} />
              <span>{mode.label}</span>
            </label>
          );
        })}
        {/* Comparison Mode Toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px',
          marginLeft: 'auto', borderLeft: '1px solid rgba(252, 211, 77,0.15)',
          background: comparisonMode ? 'rgba(253, 230, 138,0.1)' : 'transparent'
        }}>
          <i className="fas fa-columns" style={{ color: comparisonMode ? '#FDE68A' : '#71717a', fontSize: '0.68rem' }} />
          <span style={{ color: comparisonMode ? '#FEF08A' : '#71717a', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>Compare</span>
          <div
            data-testid="search-comparison-toggle"
            onClick={() => setComparisonMode(prev => !prev)}
            style={{
              width: 34, height: 18, borderRadius: 9, cursor: 'pointer',
              background: comparisonMode ? 'linear-gradient(135deg, #FDE68A, #FCD34D)' : 'rgba(255,255,255,0.12)',
              position: 'relative', transition: 'background 0.3s'
            }}
          >
            <div style={{
              width: 14, height: 14, borderRadius: '50%', background: 'white',
              position: 'absolute', top: 2, transition: 'left 0.3s',
              left: comparisonMode ? 18 : 2, boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} />
          </div>
        </div>
      </div>

      {/* Comparison mode: right-pane radio selector */}
      {comparisonMode && (
        <div style={{
          display: 'flex', gap: 0, marginBottom: 12, background: 'rgba(16,185,129,0.04)',
          borderRadius: 10, border: '1px solid rgba(16,185,129,0.2)', overflow: 'hidden',
          flexWrap: 'wrap'
        }} data-testid="search-comparison-right-panel">
          <div style={{
            padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6,
            borderRight: '1px solid rgba(16,185,129,0.15)', background: 'rgba(16,185,129,0.08)'
          }}>
            <i className="fas fa-th-large" style={{ color: '#10b981', fontSize: '0.68rem' }} />
            <span style={{ color: '#10b981', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Right Pane</span>
          </div>
          {VIZ_MODES.map((mode) => {
            const isActive = comparisonVizRight === mode.key;
            return (
              <label key={mode.key} data-testid={`search-viz-right-${mode.key}`}
                style={{
                  padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  background: isActive ? `${mode.accent}18` : 'transparent',
                  color: isActive ? '#e2e8f0' : '#71717a',
                  fontSize: '0.72rem', fontWeight: isActive ? 700 : 500,
                  borderRight: '1px solid rgba(16,185,129,0.1)',
                  transition: 'all 0.25s', userSelect: 'none',
                  borderBottom: isActive ? `2px solid ${mode.accent}` : '2px solid transparent',
                }}>
                <input type="radio" name="searchVizRight" value={mode.key}
                  checked={isActive} onChange={() => setComparisonVizRight(mode.key)}
                  style={{ accentColor: mode.accent, width: 12, height: 12, cursor: 'pointer' }} />
                <i className={mode.icon} style={{ fontSize: '0.65rem', color: isActive ? mode.accent : '#71717a' }} />
                <span>{mode.label}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* Dot Color Toggle — shown when Individual mode is active on either pane */}
      {showColorToggle && setDotColorMode && (
        <div style={{
          display: 'flex', gap: 0, marginBottom: 12, background: 'rgba(245,158,11,0.04)',
          borderRadius: 10, border: '1px solid rgba(245,158,11,0.25)', overflow: 'hidden',
        }} data-testid="dot-color-toggle-bar">
          <div style={{
            padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6,
            borderRight: '1px solid rgba(245,158,11,0.15)', background: 'rgba(245,158,11,0.08)'
          }}>
            <i className="fas fa-palette" style={{ color: '#f59e0b', fontSize: '0.72rem' }} />
            <span style={{ color: '#f59e0b', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Dot Color By
            </span>
          </div>
          {[
            { key: 'category', label: 'Category / Subcategory', icon: 'fas fa-folder', accent: '#047857' },
            { key: 'doctype', label: 'Document Type', icon: 'fas fa-file-alt', accent: '#3b82f6' },
          ].map((opt) => {
            const isActive = dotColorMode === opt.key;
            return (
              <label key={opt.key} data-testid={`dot-color-${opt.key}`}
                style={{
                  padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
                  background: isActive ? `${opt.accent}18` : 'transparent',
                  color: isActive ? '#e2e8f0' : '#71717a',
                  fontSize: '0.75rem', fontWeight: isActive ? 700 : 500,
                  borderRight: '1px solid rgba(245,158,11,0.1)',
                  transition: 'all 0.25s', userSelect: 'none',
                  borderBottom: isActive ? `2px solid ${opt.accent}` : '2px solid transparent',
                }}>
                <input type="radio" name="dotColorMode" value={opt.key}
                  checked={isActive} onChange={() => setDotColorMode(opt.key)}
                  style={{ accentColor: opt.accent, width: 13, height: 13, cursor: 'pointer' }} />
                <i className={opt.icon} style={{ fontSize: '0.68rem', color: isActive ? opt.accent : '#71717a' }} />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </>
  );
};

export default SearchVizControls;
